import {
    HttpErrorResponse,
    HttpInterceptorFn
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, throwError } from 'rxjs';

import { StorageService } from 'core/services/storage.service';
import { RouterService } from 'core/services/router.service';
import { API_ERROR_ACTION_CODES, HTTP_STATUS, MAT_DIALOGS_IDS } from 'shared/enums/common.enum';
import { LoaderService } from 'core/services/loader.service';
import { environment } from 'src/environments/environment';
import { ApiResponse } from 'shared/interfaces/response';
import { AlertService } from 'core/services/alert.service';
import { SKIP_GLOBAL_LOADER } from 'core/tokens/http-context.token';
import { DialogService } from 'core/services/dialog.service';
import { ChangePlan } from 'features/settings/components/change-plan/change-plan';

const RECENT_ERROR_TOAST_TTL_MS = 3000;
const recentErrorToasts = new Set<string>();

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
    const storageService = inject(StorageService);
    const routerService = inject(RouterService);
    const loader = inject(LoaderService);
    const alert = inject(AlertService);
    const dialogService = inject(DialogService)
    // const translation = inject(TranslationService)

    let url = req.url;

    if (!/^(?:[a-z][a-z\d+.-]*:|[/\\])/i.test(url) && !url.startsWith('assets/')) {
        url = `${environment.apiBaseUrl.replace(/\/$/, '')}/${url}`;
    }

    const apiBase = new URL(environment.apiBaseUrl);
    const destination = new URL(url, document.baseURI);
    const basePath = apiBase.pathname.replace(/\/$/, '');
    const isApiRequest = destination.origin === apiBase.origin &&
        !destination.username && !destination.password &&
        (destination.pathname === basePath || destination.pathname.startsWith(`${basePath}/`));
    // Assets and third-party requests must not receive credentials or control the session.
    if (!isApiRequest) return next(req.clone({ url }));

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const token = storageService.accessToken;
    const modifiedReq = req.clone({
        url,
        setHeaders: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'X-Timezone': timezone
        },
    });

    const skipLoader = req.context.get(SKIP_GLOBAL_LOADER);

    if(!skipLoader){
        if(loader.pendingRequests <= 0){
            queueMicrotask(() => loader.show());
        }
        loader.pendingRequests++;
    }
    
    return next(modifiedReq).pipe(
        catchError((error) => {
            const response = (error.error ?? {}) as ApiResponse<null>;
            response.errors?.forEach(e => showErrorToasterOnce(alert, e.message));
            if (error instanceof HttpErrorResponse) {
                if (error.status === HTTP_STATUS.UNAUTHORIZED) {
                    storageService.clearStorage();
                    routerService.goToLogin();
                }else if(error.status === HTTP_STATUS.FORBIDDEN) {
                    showErrorToasterOnce(alert, response.message);
                }
            }

            if(response.actionCode === API_ERROR_ACTION_CODES.NO_ACTIVE_SUBSCRIPTION){
                if(!dialogService.isDialogOpen(MAT_DIALOGS_IDS.CHANGE_PLAN)) {
                    dialogService.openDialog(ChangePlan, null, true, MAT_DIALOGS_IDS.CHANGE_PLAN);
                }
            }

            return throwError(() => error);
        }),
        finalize(() => {
            if(!skipLoader){
                loader.pendingRequests--;
                if(loader.pendingRequests <= 0){
                    queueMicrotask(() => loader.hide())
                }
            }
        })
    )
};


const showErrorToasterOnce = (alert: AlertService, message?: string) => {
    const normalizedMessage = message?.trim();
    if (!normalizedMessage || recentErrorToasts.has(normalizedMessage)) return;

    recentErrorToasts.add(normalizedMessage);
    alert.errorToaster(normalizedMessage);

    setTimeout(() => {
        recentErrorToasts.delete(normalizedMessage);
    }, RECENT_ERROR_TOAST_TTL_MS);
}
